/**
 * AuthContext — admin session.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API, REFRESH_FAILED, apiAuth, setAccessToken } from "@/lib/api";
import silentRefresh from "@/lib/silentRefresh";

const AuthCtx = createContext(null);

const needsAdminRefresh = (pathname) => pathname.startsWith("/admin");

export const AuthProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [user, setUser] = useState(null);
  const [phase, setPhase] = useState(() =>
    needsAdminRefresh(typeof window !== "undefined" ? window.location.pathname : "") ? "checking" : "anonymous",
  );

  useEffect(() => {
    let cancelled = false;

    if (!needsAdminRefresh(pathname)) {
      setAccessToken(null);
      setUser(null);
      setPhase("anonymous");
      return undefined;
    }

    (async () => {
      const data = await silentRefresh(`${API}/auth/refresh`);
      if (cancelled) return;
      if (data?.access_token) {
        setAccessToken(data.access_token);
        setUser(data.user);
        setPhase("authenticated");
      } else {
        setAccessToken(null);
        setUser(null);
        setPhase("anonymous");
      }
    })();

    return () => { cancelled = true; };
  }, [pathname]);

  useEffect(() => {
    const onFail = () => {
      setAccessToken(null);
      setUser(null);
      setPhase("anonymous");
    };
    window.addEventListener(REFRESH_FAILED, onFail);
    return () => window.removeEventListener(REFRESH_FAILED, onFail);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true },
    );
    setAccessToken(data.access_token);
    setUser(data.user);
    setPhase("authenticated");
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await apiAuth.post("/auth/logout"); } catch (_e) { /* ignore */ }
    setAccessToken(null);
    setUser(null);
    setPhase("anonymous");
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await apiAuth.get("/auth/me");
      setUser(data);
      return data;
    } catch (_e) {
      return null;
    }
  }, []);

  const setUserFromChangePassword = useCallback((updates) => {
    setUser((u) => (u ? { ...u, ...updates } : u));
  }, []);

  return (
    <AuthCtx.Provider value={{
      user, phase,
      isAuthenticated: phase === "authenticated",
      login, logout, refreshMe, setUserFromChangePassword,
    }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth() must be used within <AuthProvider>");
  return ctx;
};

export default AuthProvider;
